import * as Sentry from "@sentry/react";
import { lazy, Suspense } from "react";
import { Navigate, Route, Routes } from "react-router-dom";

import { MainContent } from "@/components";
import { AnimatedLoading } from "@/components/animated-loading";
import { PageSkeleton } from "@/components/ui/page";

const NeighborhoodPage = lazy(() =>
  import("@/pages/neighborhood").then((module) => ({
    default: module.NeighborhoodPage,
  })),
);
const UserProfileView = lazy(() => import("@/pages/user-profile"));
const UpdateUserPage = lazy(() =>
  import("@/pages/users/update").then((module) => ({
    default: module.UpdateUserPage,
  })),
);
const ProfilePage = lazy(() =>
  import("@/pages/profile").then((module) => ({
    default: module.ProfilePage,
  })),
);

const Deliverymans = lazy(() =>
  import("@/pages/deliverymans").then((module) => ({
    default: module.Deliverymans,
  })),
);
const StatsPage = lazy(() =>
  import("@/pages/stats").then((module) => ({ default: module.StatsPage })),
);
const Companies = lazy(() =>
  import("@/pages/companies").then((module) => ({ default: module.CompaniesV2 })),
);
const UsersPage = lazy(() =>
  import("@/pages/users").then((module) => ({ default: module.UsersPage })),
);
const PlansView = lazy(() => import("@/pages/plans"));
const ClientsView = lazy(() => import("@/pages/clients"));
const RegisterClientView = lazy(() => import("@/pages/clients/registerView"));
const LoginForm = lazy(() =>
  import("@/pages/login").then((module) => ({ default: module.LoginForm })),
);
const Delivery = lazy(() =>
  import("@/pages/deliveries").then((module) => ({ default: module.Delivery })),
);
const ReportsPage = lazy(() =>
  import("@/pages/reports").then((module) => ({
    default: module.Reports,
  })),
);
const RegionsPage = lazy(() =>
  import("@/pages/regions").then((module) => ({ default: module.RegionsPage })),
);
const PricesPage = lazy(() =>
  import("@/pages/regions/prices").then((module) => ({
    default: module.PricesPage,
  })),
);
const SettingsPage = lazy(() =>
  import("@/pages/settings").then((module) => ({
    default: module.SettingsPage,
  })),
);
const ContractsPage = lazy(() =>
  import("@/pages/contracts").then((module) => ({
    default: module.ContractsPage,
  })),
);
const CreateCompanyPage = lazy(() =>
  import("@/pages/companies/create").then((module) => ({
    default: module.CreateCompanyPage,
  })),
);
const UpdateCompanyPage = lazy(() =>
  import("@/pages/companies/update").then((module) => ({
    default: module.UpdateCompanyPage,
  })),
);
const ContractPriceTablesPage = lazy(() =>
  import("@/pages/contract-price-tables").then((module) => ({
    default: module.ContractPriceTablesPage,
  })),
);
const ContractPriceTableItemsPage = lazy(() =>
  import("@/pages/contract-price-table-items").then((module) => ({
    default: module.ContractPriceTableItemsPage,
  })),
);
const DynamicsV2 = lazy(() =>
  import("@/pages/dynamics").then((module) => ({
    default: module.DynamicsV2,
  })),
);
const MetricsPage = lazy(() =>
  import("@/pages/metrics").then((module) => ({
    default: module.MetricsPage,
  })),
);
const IntegrationsPage = lazy(() =>
  import("@/pages/integrations").then((module) => ({
    default: module.IntegrationsPage,
  })),
);

const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes);

const Router = () => {
  return (
    <SentryRoutes>
      <Route
        path="/"
        element={
          <Suspense fallback={<AnimatedLoading />}>
            <LoginForm />
          </Suspense>
        }
      />

      <Route path="/dashboard">
        <Route
          index
          element={
            <MainContent>
              <Navigate to="/dashboard/deliveries" />
            </MainContent>
          }
        />

        <Route path="users">
          <Route
            index
            element={
              <MainContent>
                <Suspense fallback={<PageSkeleton />}>
                  <UsersPage />
                </Suspense>
              </MainContent>
            }
          />

          <Route
            path="update/:id"
            element={
              <MainContent>
                <Suspense fallback={<PageSkeleton />}>
                  <UpdateUserPage />
                </Suspense>
              </MainContent>
            }
          />
        </Route>

        <Route path="clients">
          <Route
            index
            element={
              <MainContent>
                <Suspense fallback={<PageSkeleton />}>
                  <ClientsView />
                </Suspense>
              </MainContent>
            }
          />

          <Route
            path=":type/:id?"
            element={
              <MainContent>
                <Suspense fallback={<PageSkeleton />}>
                  <RegisterClientView />
                </Suspense>
              </MainContent>
            }
          />
        </Route>

        <Route path="plans">
          <Route
            index
            element={
              <MainContent>
                <Suspense fallback={<PageSkeleton />}>
                  <PlansView />
                </Suspense>
              </MainContent>
            }
          />
        </Route>

        <Route path="neighborhoods">
          <Route
            index
            element={
              <MainContent>
                <Suspense fallback={<PageSkeleton />}>
                  <NeighborhoodPage />
                </Suspense>
              </MainContent>
            }
          />
        </Route>

        <Route path="regions">
          <Route
            index
            element={
              <MainContent>
                <Suspense fallback={<PageSkeleton />}>
                  <RegionsPage />
                </Suspense>
              </MainContent>
            }
          />

          <Route
            path="prices/:id"
            element={
              <MainContent>
                <Suspense fallback={<PageSkeleton />}>
                  <PricesPage />
                </Suspense>
              </MainContent>
            }
          />
        </Route>

        <Route path="companies">
          <Route
            index
            element={
              <MainContent>
                <Suspense fallback={<PageSkeleton />}>
                  <Companies />
                </Suspense>
              </MainContent>
            }
          />

          <Route
            path="create"
            element={
              <MainContent>
                <Suspense fallback={<PageSkeleton />}>
                  <CreateCompanyPage />
                </Suspense>
              </MainContent>
            }
          />

          <Route
            path=":id/edit"
            element={
              <MainContent>
                <Suspense fallback={<PageSkeleton />}>
                  <UpdateCompanyPage />
                </Suspense>
              </MainContent>
            }
          />
        </Route>

        <Route
          path="deliverymans"
          element={
            <MainContent>
              <Suspense fallback={<PageSkeleton />}>
                <Deliverymans />
              </Suspense>
            </MainContent>
          }
        />

        <Route
          path="stats"
          element={
            <MainContent>
              <Suspense fallback={<PageSkeleton />}>
                <StatsPage />
              </Suspense>
            </MainContent>
          }
        />

        <Route
          path="deliveries"
          element={
            <MainContent>
              <Suspense fallback={<PageSkeleton />}>
                <Delivery />
              </Suspense>
            </MainContent>
          }
        />

        <Route
          path="reports"
          element={
            <MainContent>
              <Suspense fallback={<PageSkeleton />}>
                <ReportsPage />
              </Suspense>
            </MainContent>
          }
        />

        <Route
          path="metrics"
          element={
            <MainContent>
              <Suspense fallback={<PageSkeleton />}>
                <MetricsPage />
              </Suspense>
            </MainContent>
          }
        />

        <Route
          path="integrations"
          element={
            <MainContent>
              <Suspense fallback={<PageSkeleton />}>
                <IntegrationsPage />
              </Suspense>
            </MainContent>
          }
        />

        <Route
          path="profile"
          element={
            <MainContent>
              <Suspense fallback={<PageSkeleton />}>
                <ProfilePage />
              </Suspense>
            </MainContent>
          }
        />

        <Route
          index
          path="userProfile/:id?"
          element={
            <MainContent>
              <Suspense fallback={<PageSkeleton />}>
                <UserProfileView />
              </Suspense>
            </MainContent>
          }
        />

        <Route
          path="dynamics"
          element={
            <MainContent>
              <Suspense fallback={<PageSkeleton />}>
                <DynamicsV2 />
              </Suspense>
            </MainContent>
          }
        />

        <Route
          path="settings"
          element={
            <MainContent>
              <Suspense fallback={<PageSkeleton />}>
                <SettingsPage />
              </Suspense>
            </MainContent>
          }
        />

        <Route path="contracts">
          <Route
            index
            element={
              <MainContent>
                <Suspense fallback={<PageSkeleton />}>
                  <ContractsPage />
                </Suspense>
              </MainContent>
            }
          />

          <Route
            path=":id"
            element={
              <MainContent>
                <Suspense fallback={<PageSkeleton />}>
                  <ContractPriceTablesPage />
                </Suspense>
              </MainContent>
            }
          />

          <Route
            path=":id/price-tables/:tableId"
            element={
              <MainContent>
                <Suspense fallback={<PageSkeleton />}>
                  <ContractPriceTableItemsPage />
                </Suspense>
              </MainContent>
            }
          />
        </Route>

        <Route path="*" element={<Navigate to="/dashboard/deliveries" />} />
      </Route>

      <Route path="*" element={<h1>Esta pagina não foi encontrada</h1>} />
    </SentryRoutes>
  );
};

export default Router;
